import { Button } from '@hologram-dimension/button';
import { Panel } from '@hologram-dimension/panel';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import { useSimProfileHistory } from 'api';
import clsx from 'clsx/lite';
import { Field, Table } from 'components';
import type { HeaderItem, TableCellItem, TableRowItem } from 'components/Table/types';
import { useMemo, type FC } from 'react';
import { formatDateTime } from 'utils/dates';
import styles from './SimProfileHistoryPanel.module.css';

interface SimProfileHistoryPanelProps {
  hologramSimId?: HologramSimId;
}

const SimProfileHistoryPanel: FC<SimProfileHistoryPanelProps> = ({ hologramSimId }) => {
  const { profileHistory, isLoading, error, isValidating } = useSimProfileHistory(hologramSimId);

  const panelContents = useMemo(() => {
    if (error) {
      return (
        <PanelNotification
          variant="error"
          label="We ran into an issue loading your SIM profile history"
          actions={
            <Button
              variant="secondary"
              iconStart="Sync"
              onClick={() => {
                window.location.reload();
              }}
            >
              Reload page
            </Button>
          }
        >
          We will keep trying in the background or you can try to reload the page.
        </PanelNotification>
      );
    }

    if (isLoading) {
      return <PanelNotification>Loading...</PanelNotification>;
    }

    const headers: Array<HeaderItem> = [
      { children: 'Event (UTC)', key: 'Event (UTC)', width: 160 },
    ];

    const rows: Array<TableRowItem> = profileHistory.map((event) => {
      if (!event?.profiles) {
        return { key: '' };
      }

      const { eventTime, lifecycleEvent, eventProfileId, profiles } = event;

      const cells: Array<TableCellItem> = new Array(profiles.length + 1);
      const rowKey = `${eventProfileId}-${lifecycleEvent}-${eventTime}`;
      cells[0] = {
        key: rowKey,
        children: (
          <div className={styles.eventColumn}>
            <Field
              labelClassName={styles.eventType}
              valueClassname={styles.eventTimestamp}
              label={lifecycleEvent ?? ''}
              value={formatDateTime(eventTime, 'table')}
              direction="column"
            />
          </div>
        ),
      };

      for (let i = headers.length; i < profiles.length + 1; i++) {
        const label = `Profile ${i}`;
        headers.push({ key: label, children: label, width: 300 });
      }

      profiles.forEach((profile, index) => {
        const { profileName, iccid, simProfileId, apn } = profile;

        cells[index + 1] = {
          key: iccid ?? index.toString(),
          children: (
            <div className={styles.profileColumn}>
              <Field
                className={styles.eventField}
                labelClassName={styles.eventLabel}
                valueClassname={styles.eventValue}
                label="Profile name"
                value={profileName}
              />
              <Field
                className={styles.eventField}
                labelClassName={styles.eventLabel}
                valueClassname={clsx(styles.eventValue, styles.numeric)}
                label="ICCID"
                value={iccid}
              />
              <Field
                className={styles.eventField}
                labelClassName={styles.eventLabel}
                valueClassname={clsx(styles.eventValue, styles.numeric)}
                label="Link ID"
                value={simProfileId}
              />
              <Field
                className={styles.eventField}
                labelClassName={styles.eventLabel}
                valueClassname={styles.eventValue}
                label="Required APN"
                value={apn}
              />
            </div>
          ),
        };
      });

      // Required to ensure that the first N-1 columns size correctly
      headers.push({ key: `trailing-${event.eventTime}`, children: null });

      return { key: rowKey, cells };
    });

    return <Table headers={headers} rows={rows} />;
  }, [error, isLoading, profileHistory]);

  return (
    <Panel header="Profile history" noContentPadding isLoading={isLoading || isValidating}>
      {panelContents}
    </Panel>
  );
};

export default SimProfileHistoryPanel;
